<template>
  <div :class="$style['file-upload']">
    <label
      :class="$style['file-upload__label']"
      :for="'file-upload'"
      class=" h-margin-bottom-s">
      {{ label }} {{ required ? '*' : '' }}
    </label>
    <div
      :class="$style['file-upload__input-wrapper']"
      class="h-margin-top">
      <SvgIcon
        v-if="!fileName"
        :class="$style['file-upload__icon']"
        size="small"
        name="System/share-2-line" />
      {{ fileName ? fileName : uploadText }}
      <span v-if="fileName" :class="$style['file-upload__hover-cover']">
        <SvgIcon
          :class="$style['file-upload__icon']"
          size="small"
          name="System/share-2-line" />{{ uploadText }}
      </span>
      <input
        id="file-upload"
        type="file"
        :class="$style['file-upload__input']"
        :accept="accept"
        :placeholder="$t('form/upload-placeholder')"
        @change="onFileChange">
    </div>
    <LazyFormValidation
      v-if="
        (validation && validation.$dirty && validation.$invalid)
      "
      data-test-id="validation"
      :validation="validation"
      :custom-validation="customValidation" />
    <span :class="$style['file-upload__error']" class="h-font-size-xs h-color-error">
      {{ fileError }}
    </span>
  </div>
</template>

<script setup>
const { $lang } = useNuxtApp()

defineProps({
  id: {
    type: String,
    default: 'file-upload'
  },
  label: {
    type: String,
    default: 'Add label to FormUpload'
  },
  validation: [Boolean, Object],
  required: {
    type: Boolean,
    default: false
  },
  accept: {
    type: String,
    default: 'image/*,application/pdf'
  }
})

const emit = defineEmits(['update:modelValue'])

const fileError = ref(null)
const fileName = ref(null)

const uploadText = computed(() => {
  if ($lang === 'de') {
    return 'Datei auswählen'
  }
  if ($lang === 'en') {
    return 'Choose file'
  }
  return 'Kies bestand'
})

const onFileChange = event => {
  const file = event.target.files[0]
  if (file) {
    if (file.size > 8 * 1024 * 1024) { // 8MB limit
      fileError.value = $t('general/validation-file-size-to-big')
    } else {
      fileError.value = null
      // Handle file upload logic here
      fileName.value = file.name
      emit('update:modelValue', file)
    }
  }
}

</script>

<style lang="scss" module>
.file-upload {
  display: block;
  box-sizing: border-box;

  &__label {
    font-size: $font-size-s;
    color: $grey;
    display: block;
  }

  &__hover-cover {
    position: absolute;
    background-color: $grey-light;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    transform: translateY(100%);
    transition: 0.2s;
    opacity: 0;
  }

  &__input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $grey-lighter;
    border-radius: $border-radius;
    color: $grey;
    padding: $spacing * 1.5 $spacing * 2;
    cursor: pointer;
    transition: 0.5s;
    overflow: hidden;

    &:hover {
      background-color: $grey-light;
      border-color: $black;

      .file-upload__hover-cover {
        opacity: 1;
        transform: translateY(0);
      }
    }

    &::after {
      content: attr(data-file-name);
      font-size: $font-size-s;
      color: $grey-dark;
      margin-left: $spacing * 1.5;
    }
  }

  &__input {
    position: absolute;
    width: 100%;
    padding: $spacing * 1.5 $spacing * 2;
    color: $grey;
    border-radius: $border-radius;
    font-size: $font-size-s;
    left: 0;
    top: 0;
    cursor: pointer;
    pointer-events: all;
    transition:  0.2s;
    display: flex;
    gap: $spacing * 0.5;
    opacity: 0;

    &:focus {
      border-color: $black;
      outline: none;
      background-color: $grey-lightest;
    }

    &:hover{
      border-color: $black;
      background-color: $grey-lighter;
    }
  }

  &__icon {
    margin-right: $spacing * 2;
    color: $grey;
  }

  &__error {
    display: block;
    position: relative;
    height: 12px;
  }

}
</style>
